import type { LeagueContext } from '@api-service/league-context';
import type {
  TeamFixturePreview,
  TeamWithFixturePreview
} from '@api-service/team-fixtures-preview';

/**
 * - `open`: the poll is for the next gameweek, so members can vote.
 * - `closed`: the poll is for the gameweek whose deadline has just passed, so
 *   its final result shows until the editors publish the next week.
 * - `hidden`: any other gameweek, so a poll nobody updated never lingers.
 */
export type CaptaincyPollStatus = 'open' | 'closed' | 'hidden';

const PERCENT = 100;

const VENUE_LABELS: Record<TeamFixturePreview['venue'], string> = {
  home: 'H',
  away: 'A'
};

export const NO_FIXTURE_LABEL = 'No fixture';

export const getCaptaincyPollStatus = (
  pollGameweek: number,
  context: Pick<LeagueContext, 'currentGameweek' | 'nextGameweek'> | null
): CaptaincyPollStatus => {
  if (pollGameweek === context?.nextGameweek?.number) return 'open';
  if (pollGameweek === context?.currentGameweek?.number) return 'closed';
  return 'hidden';
};

/** A player's share of the votes as a whole percentage. */
export const toVoteShare = (votes: number, totalVotes: number) =>
  totalVotes === 0 ? 0 : Math.round((votes / totalVotes) * PERCENT);

export const DEFAULT_CAPTAINCY_POLL_QUESTION = (gameweek: number) =>
  `Who are you captaining in Gameweek ${gameweek}?`;

// A kickoff not yet announced sorts last, so the confirmed leg of a double
// gameweek comes first.
const kickoffTime = ({ kickoffAt }: TeamFixturePreview) =>
  kickoffAt ? new Date(kickoffAt).getTime() : Number.POSITIVE_INFINITY;

const formatOpponents = (fixtures: TeamFixturePreview[]) =>
  fixtures.length === 0
    ? NO_FIXTURE_LABEL
    : `v ${[...fixtures]
        .sort((first, second) => kickoffTime(first) - kickoffTime(second))
        .map(
          ({ opponent, venue }) =>
            `${opponent.shortName} (${VENUE_LABELS[venue]})`
        )
        .join(', ')}`;

/**
 * Who each club plays in a gameweek, keyed by its short name: "v BOU (H)",
 * "v BOU (H), ARS (A)" in a double gameweek, or "No fixture" in a blank.
 * Empty when no club has a fixture in the gameweek, which means the list does
 * not cover it, so the poll leaves the line out rather than calling every
 * club blank.
 */
export const toGameweekOpponents = (
  teams: Pick<TeamWithFixturePreview, 'shortName' | 'fixtures'>[],
  gameweek: number
): Map<string, string> => {
  const fixturesByTeam = teams.map(({ shortName, fixtures }) => ({
    shortName,
    fixtures: fixtures.filter((fixture) => fixture.gameweek === gameweek)
  }));
  if (fixturesByTeam.every(({ fixtures }) => fixtures.length === 0)) {
    return new Map();
  }
  return new Map(
    fixturesByTeam.map(({ shortName, fixtures }) => [
      shortName,
      formatOpponents(fixtures)
    ])
  );
};
